import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Image from '@/components/base/Image'
import Section from '@/components/base/Section'
import SVGIcon from '@/components/base/SVGIcon'
import Typography, { TypographyProps } from '@/components/base/Typography'

import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

export type HeroProps = HTMLAttributes<HTMLElement> &
  Partial<{
    image: WPImage
    overline: string
    title_variant: TypographyProps['variant']
    title_component: TypographyProps['component']
    title: string
    paragraph: string
    button: WPLink
  }>

const Hero = ({
  overline,
  title,
  title_variant,
  title_component,
  image,
  paragraph,
  button,
  className,
  ...props
}: HeroProps) => {
  return (
    <Section
      className={cn([
        'h-svh-vh -mt-header-full-height w-full pt-header-full-height',
        className
      ])}
      {...props}
    >
      <div className="h-full w-full pb-actions-height">
        <Container className="h-full py-[2rem] md:pb-[6rem] md:pt-[1.5rem]">
          <div
            className={cn([
              'relative h-full w-full overflow-hidden rounded-[3rem] bg-dark-blue',
              'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:rounded-[3rem] after:bg-black/50'
            ])}
          >
            <Image
              source={image}
              priority={true}
              fill
              fit="cover"
              className="rounded-[3rem]"
            />
            <div className="relative z-10 grid h-full w-full items-end p-margin md:items-center">
              <div
                className={cn([
                  'grid gap-y-[1rem] md:gap-y-[2rem]',
                  'md:max-w-[48.5rem] lg:max-w-[58.5rem]'
                ])}
              >
                {overline ? (
                  <Typography variant="label" className="text-white">
                    {overline}
                  </Typography>
                ) : null}

                {title ? (
                  <Typography
                    variant={title_variant}
                    component={title_component}
                    className="text-white"
                  >
                    {title}
                  </Typography>
                ) : null}

                {paragraph || button ? (
                  <div
                    className={cn([
                      'grid gap-y-[3rem]',
                      'md:max-w-[40rem] lg:max-w-[44rem]'
                    ])}
                  >
                    {paragraph ? (
                      <Typography
                        variant="body"
                        className="text-white"
                        paragraph
                      >
                        {paragraph}
                      </Typography>
                    ) : null}

                    {button ? (
                      <div className="flex">
                        <Button
                          variant="link"
                          asChild
                          startIcon={
                            <SVGIcon
                              symbol="arrow-right-icon"
                              className="h-[1.1rem] w-[0.6rem] shrink-0 fill-heading"
                            />
                          }
                        >
                          <Link href={button.url} target={button.target}>
                            <span
                              data-label={button.title}
                              className="after:text-white"
                            >
                              <Typography
                                variant="label"
                                className="text-white"
                              >
                                {button.title}
                              </Typography>
                            </span>
                          </Link>
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </Container>
      </div>
    </Section>
  )
}

export default Hero
